
 
import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from '@/hooks/use-toast';
import { adminService } from '@/services/adminService';
import { ArrowLeft, LogOut, UploadCloud, Loader2, AlertTriangle, List, BarChart2, Database, Wand2, Upload, Image as ImageIcon } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';
 
const AiUsageLogViewer = lazy(() => import('@/components/admin/AiUsageLogViewer'));
const ResumeArchiveViewer = lazy(() => import('@/components/admin/ResumeArchiveViewer'));
 
const initialPrompts = {
    critique: `Act as a professional resume critique bot.\nCritique the following resume text. Highlight grammar errors, formatting issues, missing achievements, and suggest improvements for ATS compliance, clarity, and professional standards. Provide detailed improvement suggestions in bullet points using Markdown formatting.`,
    coverletter: `Act as a professional cover letter generator.\nGenerate a professional cover letter based on the following resume text. Make it enthusiastic and tailored for job applications.\n{{#if jobDescription}}Tailor it for the job description.{{else}}Make it a generic cover letter suitable for various corporate roles.{{/if}}\nThe tone should be confident, and appropriate. Ensure the output is well-structured using Markdown.`,
    recruiterview: `Analyze the following resume from a recruiter's perspective.\nPoint out strengths, weaknesses, and overall impression. Output reasons to shortlist or reject, and provide actionable suggestions for improvement. Format the response clearly using Markdown headings and lists.`,
};
 
const LoadingFallback = () => (
    <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
);
 
export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDevBypassActive, setIsDevBypassActive] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [selectedTrainingFeature, setSelectedTrainingFeature] = useState<string>('resume_critique');
  const [promptText, setPromptText] = useState(initialPrompts.critique);
 
  const [capabilityDomain, setCapabilityDomain] = useState("");
  const [capabilityFile, setCapabilityFile] = useState<File | null>(null);
  const [capabilityFileText, setCapabilityFileText] = useState("");
  const [isParsingFile, setIsParsingFile] = useState(false);
 // AI Logs Filter State
const [selectedLogFeature, setSelectedLogFeature] = useState("all");
const [dateFrom, setDateFrom] = useState("");
const [dateTo, setDateTo] = useState("");
const [refreshLogs, setRefreshLogs] = useState(0);
const [totalLogs, setTotalLogs] = useState(0);

  const [vtechLogoFile, setVtechLogoFile] = useState<File | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  // Resume Archive State

const [archiveModes, setArchiveModes] = useState<string[]>(["all"]);
const [isModeOpen, setIsModeOpen] = useState(false);
const [archiveFrom, setArchiveFrom] = useState("");
const [archiveTo, setArchiveTo] = useState("");
const [archiveRefresh, setArchiveRefresh] = useState(0);
const [totalDownloadsAI, setTotalDownloadsAI] = useState(0);

const [totalResumes, setTotalResumes] = useState(0);
const [totalCalls, setTotalCalls] = useState(0);
const [totalDownloads, setTotalDownloads] = useState(0);
 
  useEffect(() => {
    switch(selectedTrainingFeature) {
        case 'resume_critique': setPromptText(initialPrompts.critique); break;
        case 'cover_letter': setPromptText(initialPrompts.coverletter); break;
        case 'recruiter_view': setPromptText(initialPrompts.recruiterview); break;
        case 'capability_statement': setPromptText(''); break;
        default: setPromptText(""); break;
    }
  }, [selectedTrainingFeature]);
 
  useEffect(() => {
    setIsMounted(true);
    const bypassStatus = localStorage.getItem('devBypassActive') === 'true';
    setIsDevBypassActive(bypassStatus);
 
    if (bypassStatus) {
      setIsLoadingAuth(false);
      return;
    }
 
    // Check if admin is authenticated
    const checkAuth = async () => {
      try {
        if (adminService.isAuthenticated()) {
          const response = await adminService.verifyToken();
          if (response.success) {
            setCurrentUser(response.user);
          } else {
            throw new Error('Token verification failed');
          }
        } else {
          throw new Error('No token found');
        }
      } catch (error: any) {
        console.error('Auth check failed:', error);
        toast({
          variant: "destructive",
          title: "Authentication Required",
          description: "Please login to access the admin panel.",
        });
        navigate('/admin-login');
      } finally {
        setIsLoadingAuth(false);
      }
    };
 
    checkAuth();
  }, [navigate, toast]);
 
  const handlePromptInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => setPromptText(e.target.value);
 
  const handlePromptSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setIsSubmitting(true);
      console.log(`Simulating save for feature: ${selectedTrainingFeature}`);
      console.log("New prompt text:", promptText);
      setTimeout(() => {
          toast({ title: "Prompt Saved (Simulated)", description: `The prompt for ${selectedTrainingFeature} has been updated.` });
          setIsSubmitting(false);
      }, 1000);
  };
 
  const parseFileText = async (file: File): Promise<string> => {
    const arrayBuffer = await file.arrayBuffer();
    if (file.type.includes('pdf')) {
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        let text = '';
        for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const content = await page.getTextContent();
            text += content.items.map(item => 'str' in item ? item.str : '').join(' ');
        }
        return text;
    } else if (file.type.includes('word')) {
        const result = await mammoth.extractRawText({ arrayBuffer });
        return result.value;
    }
    return '';
  };
 
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
        setCapabilityFile(file);
        setIsParsingFile(true);
        try {
            const text = await parseFileText(file);
            setCapabilityFileText(text);
        } catch (error) {
            toast({ variant: "destructive", title: "File Parsing Failed", description: "Could not read text from the document." });
            console.error(error);
        } finally {
            setIsParsingFile(false);
        }
    }
  };
 
  const handleCapabilityExampleSubmit = async () => {
      if (!capabilityDomain.trim() || !capabilityFileText.trim()) {
          toast({ variant: "destructive", title: "Missing Information", description: "Please provide a domain and a valid training file." });
          return;
      }
      setIsSubmitting(true);
      try {
          // TODO: Implement backend API for capability statement examples
          toast({ title: "Success", description: "New training example saved (simulated)." });
          setCapabilityDomain("");
          setCapabilityFile(null);
          setCapabilityFileText("");
      } catch (error: any) {
          toast({ variant: "destructive", title: "Save Failed", description: error.message });
      } finally {
          setIsSubmitting(false);
      }
  };
 
  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
        if (file.type !== 'image/svg+xml') {
            toast({ variant: "destructive", title: "Invalid File Type", description: "Please upload an SVG file for the logo." });
            return;
        }
        setVtechLogoFile(file);
    }
  };
 
  const handleLogoUpload = async () => {
    if (!vtechLogoFile) {
        toast({ variant: "destructive", title: "No File Selected", description: "Please select an SVG logo file to upload." });
        return;
    }
    setIsUploadingLogo(true);
    try {
        // TODO: Implement backend API for logo upload
        toast({ title: "Logo Uploaded", description: "The vTech logo has been updated (simulated). Changes will be visible on page refresh." });
        setVtechLogoFile(null);
    } catch (error: any) {
        toast({ variant: "destructive", title: "Upload Failed", description: error.message });
    } finally {
        setIsUploadingLogo(false);
    }
  };
  // AI Logs Search
const handleLogSearch = () => {
  setAppliedLogFilters({
    feature: selectedLogFeature,
    from: dateFrom,
    to: dateTo
  });

  setRefreshLogs(prev => prev + 1);
};
const [appliedFilters, setAppliedFilters] = useState({
  mode: ["all"],
  from: "",
  to: ""
});
const [appliedLogFilters, setAppliedLogFilters] = useState({
  feature: "all",
  from: "",
  to: ""
});
const modeOptions = [
  { label: "All", value: "all" },
  { label: "Cover Letter", value: "coverletter" },
  { label: "Critique", value: "critique" },
  { label: "Recruiter View", value: "recruiterview" }
];
 
  const handleLogout = async () => {
    if (isDevBypassActive) {
      localStorage.removeItem('devBypassActive');
      setIsDevBypassActive(false);
      toast({ title: "Dev Bypass Deactivated", description: "Redirecting to login." });
      navigate('/admin-login');
      return;
    }
    
    try {
      await adminService.logout();
      setCurrentUser(null);
      toast({ title: "Logged Out", description: "You have been logged out successfully." });
      navigate('/admin-login');
    } catch (error: any) {
      console.error('Logout error:', error);
      // Clear token anyway
      adminService.clearToken();
      setCurrentUser(null);
      toast({ title: "Logged Out", description: "You have been logged out." });
      navigate('/admin-login');
    }
  };
 
  const featureNameMap: { [key: string]: string } = {
      resume_critique: "Resume Critique Bot",
      cover_letter: "Cover Letter Generator",
      recruiter_view: "How Recruiters See You",
      capability_statement: "Capability Statement Generator"
  };
 
  const renderTrainingForm = () => {
    if (selectedTrainingFeature === 'capability_statement') {
        return (
             <div className="space-y-6 border-t pt-4">
                <div>
                    <h3 className="text-lg font-medium">Upload Training Examples</h3>
                    <p className="text-sm text-muted-foreground">Upload past capability statements to train the AI. Tag each with a domain/industry.</p>
                </div>
                <div className="space-y-2">
                    <Label htmlFor="capability-domain">Domain / Industry*</Label>
                    <Input id="capability-domain" placeholder="e.g., Federal Healthcare, SLED, Financial Services" value={capabilityDomain} onChange={e => setCapabilityDomain(e.target.value)} />
                </div>
                <div className="space-y-2">
                     <Label htmlFor="capability-file">Training Document (PDF/DOCX)*</Label>
                     <Input id="capability-file" type="file" onChange={handleFileChange} accept=".pdf,.doc,.docx" />
                     {capabilityFile && <p className="text-sm text-muted-foreground">Selected: {capabilityFile.name}</p>}
                </div>
                 {isParsingFile && <div className="flex items-center text-sm text-muted-foreground"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Parsing document...</div>}
                {capabilityFileText && (
                    <div className="space-y-2">
                        <Label>Parsed Text (Read-only)</Label>
                        <Textarea value={capabilityFileText} readOnly rows={8} className="font-mono text-xs bg-secondary/50" />
                    </div>
                )}
                <Button onClick={handleCapabilityExampleSubmit} disabled={isSubmitting ?? isParsingFile ?? !capabilityFileText}>
                    {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                    Save Training Example
                </Button>
            </div>
        );
    }
 
    return (
        <div className="border-t pt-4 space-y-4">
            <div>
              <h3 className="text-lg font-medium">Edit Base Prompt for: {featureNameMap[selectedTrainingFeature]}</h3>
              <p className="text-sm text-muted-foreground">Modify the base prompt used by the AI to generate responses. Use Handlebars syntax <code>{'{{{variable_name}}}'}</code> for dynamic inputs.</p>
            </div>
            <form onSubmit={handlePromptSubmit} className="space-y-4">
                <div>
                    <Label htmlFor="prompt-editor">Base Prompt</Label>
                    <Textarea id="prompt-editor" name="prompt" value={promptText} onChange={handlePromptInputChange} rows={15} className="font-mono text-xs" />
                </div>
                <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={isSubmitting}>{isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wand2 className="mr-2 h-4 w-4" />} Save Prompt Changes</Button>
            </form>
        </div>
    );
  };
 
  const renderBrandingManager = () => {
    return (
        <Card className="shadow-lg">
            <CardHeader>
                <CardTitle className="flex items-center text-xl">
                    <ImageIcon className="mr-3 h-6 w-6 text-primary" />
                    Branding Manager
                </CardTitle>
                <CardDescription>Manage brand assets like logos for use across the application.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                 <div>
                    <Label htmlFor="vtech-logo-upload">Upload vTech Logo</Label>
                    <p className="text-sm text-muted-foreground mb-2">Upload the main vTech logo here. It must be an SVG file. This will replace the logo used in generated documents.</p>
                    <Input id="vtech-logo-upload" type="file" onChange={handleLogoFileChange} accept=".svg" />
                    {vtechLogoFile && <p className="text-sm text-muted-foreground mt-2">Selected: {vtechLogoFile.name}</p>}
                </div>
                <Button onClick={handleLogoUpload} disabled={isUploadingLogo ?? !vtechLogoFile}>
                    {isUploadingLogo ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UploadCloud className="mr-2 h-4 w-4" />}
                    Upload Logo
                </Button>
            </CardContent>
        </Card>
    );
  };
 
  if (!isMounted || isLoadingAuth && !isDevBypassActive) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }
 
  const displayedEmail = isDevBypassActive ? "dev@bypass.com" : currentUser?.email;
  const aiFeatureMap: Record<string, string> = {
  resume_critique: 'critique',
  cover_letter: 'coverletter',
  recruiter_view: 'recruiterview',
  capability_statement: 'capability-statement',
  digital_modernization: 'digital-modernization',
  custom_app_migration: 'custom-app-migration',
  case_salesforce: 'case-management-salesforce',
  itsm_servicenow: 'itsm-servicenow',
  infra_migration: 'infrastructure-migration'
};
 
  return (
    <div className="flex flex-col min-h-screen bg-main-bg text-foreground">
      <header className="bg-card shadow-md bg-main-bg sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <h1 className="text-2xl font-bold">Admin Dashboard</h1>
          <div className="flex items-center gap-4">
             <Button variant="outline" asChild>
                <Link to="/">
                  <ArrowLeft /> Back to Home
                </Link>
              </Button>
            <Button variant="ghost" onClick={handleLogout} className="text-destructive hover:bg-destructive/10">
              <LogOut /> Logout
            </Button>
          </div>
        </div>
      </header>
 
      <main className="container mx-auto px-4 py-8 flex-grow">
        {isMounted && isDevBypassActive && (
          <Card className="mb-6 border-yellow-500 bg-yellow-500/10">
            <CardContent className="p-4">
              <div className="flex items-center">
                <AlertTriangle className="h-6 w-6 mr-3 text-yellow-600" />
                <div>
                  <CardTitle className="text-yellow-700">DEVELOPMENT BYPASS MODE ACTIVE</CardTitle>
                  <CardDescription className="text-yellow-600">
                    Backend authentication is bypassed. Data submissions will be simulated.
                  </CardDescription>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        <p className="mb-6 text-muted-foreground">Welcome, {displayedEmail ?? "Admin"}. Manage vTech Navigator content and settings.</p>
 
        <Tabs defaultValue="resume-archive" className="w-full">
            <TabsList className="grid w-full grid-cols-1 sm:grid-cols-4">
                <TabsTrigger value="resume-archive"><List />Resume Archive</TabsTrigger>
                <TabsTrigger value="ai-logs"><BarChart2 />AI Usage Logs</TabsTrigger>
                <TabsTrigger value="training-data"><Database />Training Data</TabsTrigger>
                <TabsTrigger value="branding"><ImageIcon />Branding</TabsTrigger>
            </TabsList>
           <TabsContent value="resume-archive" className="mt-4">
  <Card className="shadow-lg border border-border/50 bg-card">

    <CardHeader className="flex flex-row items-center justify-between">
  <div>
    <CardTitle className="text-2xl font-semibold">
      Resume Archive & Contact Extractor
    </CardTitle>
    <CardDescription>
      Filter and manage uploaded resumes.
    </CardDescription>
  </div>

  <Button
    onClick={() => {
      document.dispatchEvent(new Event("resume-export"));
    }}
    className="bg-blue-600 hover:bg-blue-700 text-white"
  >
    Export CSV
  </Button>
</CardHeader>

    <CardContent className="space-y-4">

      {/* SUMMARY LINE */}
      <div className="flex justify-between items-center bg-muted/20 px-6 py-2 rounded-lg border border-border/40">

        <div className="text-base font-semibold">
          Total Resumes:
          <span className="ml-2 text-lg text-primary">
            {totalResumes}
          </span>
        </div>

        <div className="text-base font-semibold">
          Total Calls:
          <span className="ml-2 text-lg text-blue-500">
            {totalCalls}
          </span>
        </div>

        <div className="text-base font-semibold">
          Total Downloads:
          <span className="ml-2 text-lg text-green-500">
            {totalDownloads}
          </span>
        </div>

      </div>

      {/* FILTER SECTION */}
      <div className="bg-muted/10 p-4 rounded-lg border border-border/40">
        <div className="flex flex-wrap items-end gap-x-2 gap-y-3">

          {/* Mode */}
         <div className="space-y-1 w-[160px] ">
  <Label>Mode</Label>

  <div className="relative">
    <button
      type="button"
      className="w-[160px] h-9 px-3 border rounded-md bg-background text-left text-sm"
      onClick={() => setIsModeOpen(prev => !prev)}
    >
      {archiveModes.length === 0 || archiveModes.includes("all")
        ? "All"
        : archiveModes.join(", ")}
    </button>

    {isModeOpen && (
      <div className="absolute z-50 mt-1 w-[160px] bg-card border rounded-md shadow-md p-2 space-y-2">

        {modeOptions.map(option => (
          <label
            key={option.value}
            className="flex items-center gap-2 text-sm cursor-pointer"
          >
            <input
              type="checkbox"
              checked={
                option.value === "all"
                  ? archiveModes.includes("all")
                  : archiveModes.includes(option.value)
              }
              onChange={(e) => {
                if (option.value === "all") {
                  if (e.target.checked) {
                    setArchiveModes(["all"]);
                  } else {
                    setArchiveModes([]);
                  }
                } else {
                  let updated = [...archiveModes.filter(m => m !== "all")];

                  if (e.target.checked) {
                    updated.push(option.value);
                  } else {
                    updated = updated.filter(m => m !== option.value);
                  }

                  setArchiveModes(updated);
                }
              }}
            />
            {option.label}
          </label>
        ))}

      </div>
    )}
  </div>
</div>
          {/* From */}
          <div className="space-y-1 w-[160px]">
            <Label>From</Label>
            <Input
              type="date"
              className="h-9 bg-[#0f172a] text-white border-border custom-date"
              value={archiveFrom}
              onChange={(e) => setArchiveFrom(e.target.value)}
            />
          </div>

          {/* To */}
          <div className="space-y-1 w-[160px]">
            <Label>To</Label>
            <Input
              type="date"
             className="h-9 bg-[#0f172a] text-white border-border custom-date"
              value={archiveTo}
              onChange={(e) => setArchiveTo(e.target.value)}
            />
          </div>

          {/* Apply */}
      <Button
  onClick={() => {
    setAppliedFilters({
      mode: archiveModes,
      from: archiveFrom,
      to: archiveTo
    });
    setArchiveRefresh(prev => prev + 1);
  }}
>
  Apply
</Button>

          {/* Reset */}
          <Button
  variant="secondary"
  onClick={() => {
    setArchiveModes(["all"]);
    setArchiveFrom("");
    setArchiveTo("");

    setAppliedFilters({
      mode: ["all"],
      from: "",
      to: ""
    });

    setArchiveRefresh(prev => prev + 1);
  }}
>
  Reset
</Button>

        </div>
      </div>

      {/* TABLE */}
      
        <Suspense fallback={<LoadingFallback />}>
          <ResumeArchiveViewer
  mode={appliedFilters.mode}
  fromDate={appliedFilters.from}
  toDate={appliedFilters.to}
  refreshTrigger={archiveRefresh}
  onSummaryChange={(summary) => {
    setTotalResumes(summary.totalResumes);
    setTotalCalls(summary.totalCalls);
    setTotalDownloads(summary.totalDownloads);
  }}
/>
        </Suspense>
    

    </CardContent>
  </Card>
</TabsContent>
<TabsContent value="ai-logs" className="mt-6">
  <Card className="shadow-lg border border-border/50 bg-card">

    {/* HEADER */}
    <CardHeader className="flex flex-row items-center justify-between pb-4">
      <div>
        <CardTitle className="text-2xl font-semibold">
          AI Feature Usage Logs
        </CardTitle>
        <CardDescription className="mt-1 text-sm">
          Filter and view Gemini feature interactions.
        </CardDescription>
      </div>

      <Button
  onClick={() => {
    document.dispatchEvent(new Event("ai-logs-export"));
  }}
  className="w-[200px] h-9 bg-blue-600 hover:bg-blue-700 text-white font-medium"
>
  Export
</Button>
    </CardHeader>

    <CardContent className="space-y-6">

      {/* STATS BAR */}
      <div className="flex justify-between items-center bg-muted/20 px-6 py-3 rounded-lg border border-border/40">

        <div className="text-base font-semibold">
          Total Logs:
          <span className="ml-2 text-lg text-primary">
            {totalLogs}
          </span>
        </div>

        <div className="text-base font-semibold">
         Download Clicks:
<span className="ml-2 text-lg text-blue-500">
  {totalDownloadsAI}
</span>
        </div>

      </div>

      {/* FILTER SECTION */}
      <div className="bg-muted/10 p-4 rounded-lg border border-border/40">

        <div className="flex flex-wrap items-end gap-4">

          {/* Feature */}
          <div className="space-y-1 min-w-[180px]">
            <Label className="text-sm font-medium">Feature</Label>
            <Select
              value={selectedLogFeature}
              onValueChange={setSelectedLogFeature}
            >
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Select feature" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="resume_critique">Resume Critique</SelectItem>
                <SelectItem value="cover_letter">Cover Letter</SelectItem>
                <SelectItem value="recruiter_view">Recruiter View</SelectItem>
                <SelectItem value="capability_statement">Capability Statement</SelectItem>
                <SelectItem value="digital_modernization">Digital Modernization</SelectItem>
                <SelectItem value="custom_app_migration">Custom App Migration</SelectItem>
                <SelectItem value="case_salesforce">Case → Salesforce</SelectItem>
                <SelectItem value="itsm_servicenow">ITSM → ServiceNow</SelectItem>
                <SelectItem value="infra_migration">Infrastructure Migration</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* From */}
          <div className="space-y-1 w-[160px]">
            <Label className="text-sm font-medium">From</Label>
            <Input
              type="date"
              className="h-9 bg-[#0f172a] text-white border-border custom-date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>

          {/* To */}
          <div className="space-y-1 w-[160px]">
            <Label className="text-sm font-medium">To</Label>
            <Input
              type="date"
              className="h-9 bg-[#0f172a] text-white border-border custom-date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>

          {/* Search */}
          <div className="w-[130px]">
            <Button
              onClick={handleLogSearch}
              className="w-full h-9 bg-blue-600 hover:bg-blue-700 text-white font-medium"
            >
              Search
            </Button>
          </div>

          {/* Reset */}
          <div className="w-[110px]">
            <Button
              variant="secondary"
              className="w-full h-9"
              onClick={() => {
  setSelectedLogFeature("all");
  setDateFrom("");
  setDateTo("");

  setAppliedLogFilters({
    feature: "all",
    from: "",
    to: ""
  });

  setRefreshLogs(prev => prev + 1);
}}
            >
              Reset
            </Button>
          </div>

        </div>
      </div>

      {/* LOG TABLE */}
      <div className="border rounded-lg p-2 bg-background">
        <Suspense fallback={<LoadingFallback />}>
          <AiUsageLogViewer
  feature={aiFeatureMap[selectedLogFeature] || 'all'}
  dateFrom={dateFrom}
  dateTo={dateTo}
  refreshTrigger={refreshLogs}
  totalDownloadsAI={totalDownloadsAI}
  onTotalChange={(total: number, downloads: number) => {
    setTotalLogs(total);
    setTotalDownloadsAI(downloads);
  }}
/>
        </Suspense>
      </div>

    </CardContent>
  </Card>
</TabsContent>




            <TabsContent value="training-data" className="mt-4">
                 <Card className="shadow-lg">
                    <CardHeader>
                      <CardTitle className="flex items-center text-xl">
                          <Database className="mr-3 h-6 w-6 text-primary" />
                          AI Prompt & Training Manager
                      </CardTitle>
                      <CardDescription>Select an AI feature to manage its base prompt and fine-tune its behavior.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div>
                            <Label htmlFor="training-feature-select">Select AI Feature to Edit</Label>
                             <Select value={selectedTrainingFeature} onValueChange={setSelectedTrainingFeature}>
                                <SelectTrigger id="training-feature-select" className="w-full md:w-1/2">
                                    <SelectValue placeholder="Select a feature..." />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="resume_critique">Resume Critique Bot</SelectItem>
                                    <SelectItem value="cover_letter">Cover Letter Generator</SelectItem>
                                    <SelectItem value="recruiter_view">How Recruiters See You</SelectItem>
                                    <SelectItem value="capability_statement">Capability Statement</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                       
                        {renderTrainingForm()}
 
                    </CardContent>
                </Card>
            </TabsContent>
            <TabsContent value="branding" className="mt-4">
                {renderBrandingManager()}
            </TabsContent>
        </Tabs>
      </main>
 
      <footer className="py-4 text-center text-muted-foreground text-sm border-t border-border">
        © {new Date().getFullYear()} vTech Navigator Admin Panel {isMounted && isDevBypassActive && " (Dev Bypass Active)"}
      </footer>
    </div>
  );
}
 